import { useState, useMemo, useCallback, useEffect } from "react";
import { Box, Card, Divider } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { format, parse, addDays, isSameDay } from "date-fns";
import {
  GridColDef,
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
} from "@mui/x-data-grid-premium";
import PatientDatas from "src/modules/medcard/patient-datas";
import GeneralHealthInformation from "./general-health-information";
import DietPopup from "./pages/diet-add";
import OpenComponentDialog from "src/components/shared-components/modal/openComponentModal";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";
import ByzMuiAddButton from "src/components/core-components/mui/buttons/ByzMuiAddButton";
import ByzMuiPrintButton from "src/components/core-components/mui/buttons/ByzMuiPrintButton";
import ByzMuiDataGrid from "src/components/core-components/mui/data-grid";
import { ByzMuiCheckBox } from "src/components/core-components/mui/ByzMuiCheckBox";
import ByzMuiOpenProcessMenu from "src/components/core-components/mui/ByzMuiOpenProcessMenu";
import ByzDaySelectButton from "src/components/core-components/mui/ByzDaySelect";
import ByzMuiSendButton from "src/components/core-components/mui/buttons/ByzMuiSendButton";
import { useTranslate } from "src/locales";
import ByzGrdEditButton from "src/components/core-components/mui/buttons-grid/ByzGrdEditButton";
import EditPage from "./pages/diet-add/edit-page";
//IDOGAN;449733;25042025
type Approval = "Beklemede" | "Gönderildi";

export default function DietProceduresCreate() {
  const { t } = useTranslate();
  const [editDialogOpen, setEditDialogOpen] = useState(false); //IDOGAN;452471;05052025;
  const [editRow, setEditRow] = useState<any>(null); //IDOGAN;452471;05052025;
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [exported, setExported] = useState<{
    parentRows: any[];
    parentColumns: GridColDef[];
    childMap: Record<string, any[]>;
    childColumns: GridColDef[];
  } | null>(null);

  const [parentApproval, setParentApproval] = useState<Approval>("Beklemede");
  const [childApproval, setChildApproval] = useState<Record<string, Approval>>(
    {}
  );
  const [selectionModel, setSelectionModel] = useState<string[]>([]);
  const [dayOffset, setDayOffset] = useState(0);

  const handleExportGrid = useCallback(
    ({
      parentRows,
      parentColumns,
      childMap,
      childColumns,
    }: {
      parentRows: any[];
      parentColumns: GridColDef[];
      childMap: Record<string, any[]>;
      childColumns: GridColDef[];
    }) => {
      const pRows = parentRows.map((r) => ({ ...r, id: `p_${r.id}` }));
      const cMap: Record<string, any[]> = {};
      Object.entries(childMap).forEach(([date, rows]) => {
        cMap[date] = rows.map((r) => ({ ...r, id: `c_${date}_${r.id}` }));
      });
      setExported({
        parentRows: pRows,
        parentColumns,
        childMap: cMap,
        childColumns,
      });
      setSelectionModel([]);
      setAddDialogOpen(false);
    },
    []
  );

  useEffect(() => {
    if (!exported) return void setChildApproval({});
    const init: Record<string, Approval> = {};
    Object.values(exported.childMap)
      .flat()
      .forEach((r) => (init[r.id] = "Beklemede"));
    setChildApproval(init);
  }, [exported]);

  const displayedParentRows = useMemo(() => {
    if (!exported) return [];
    const target = addDays(new Date(), dayOffset);
    return exported.parentRows.filter((r) =>
      isSameDay(parse(r.diyetZamani, "dd.MM.yyyy", new Date()), target)
    );
  }, [exported, dayOffset]);

  const displayedChildMap = useMemo(() => {
    if (!exported) return {};
    const key = format(addDays(new Date(), dayOffset), "dd.MM.yyyy");
    return { [key]: exported.childMap[key] ?? [] };
  }, [exported, dayOffset]);

  const allRowIds = useMemo(() => {
    if (!exported) return [];
    const p = displayedParentRows.map((r) => r.id as string);
    const c = Object.values(displayedChildMap)
      .flat()
      .map((r) => r.id as string);
    return [...p, ...c];
  }, [exported, displayedParentRows, displayedChildMap]);

  const isAllSelected =
    allRowIds.length > 0 &&
    allRowIds.every((id) => selectionModel.includes(id));
  const isIndeterminate = selectionModel.length > 0 && !isAllSelected;

  const handleSendSelected = useCallback(() => {
    // 1) seçili parentleri tespit edip gönder
    const selParents = displayedParentRows
      .map((r) => r.id.toString())
      .filter((id) => selectionModel.includes(id));
    if (selParents.length) {
      setParentApproval("Gönderildi");
    }

    // 2) seçili childları tespit edip gönder
    const selChildren = Object.values(displayedChildMap)
      .flat()
      .map((r) => r.id.toString())
      .filter((id) => selectionModel.includes(id));
    if (selChildren.length) {
      setChildApproval((prev) => {
        const nxt = { ...prev };
        selChildren.forEach((id) => (nxt[id] = "Gönderildi"));
        return nxt;
      });
    }
  }, [displayedParentRows, displayedChildMap, selectionModel]);
  // Parent action column
  const parentActionsColumn: GridColDef = useMemo(
    () => ({
      field: "__actions__",
      headerName: "",
      width: 100,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderHeader: () => (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <ByzMuiCheckBox
            size="small"
            indeterminate={isIndeterminate}
            checked={isAllSelected}
            onChange={() => setSelectionModel(isAllSelected ? [] : allRowIds)}
          />
        </Box>
      ),
      renderCell: (params) => {
        const pid = params.id.toString();
        const key = params.row.diyetZamani as string;
        const childIds = (displayedChildMap[key] || []).map((r) =>
          r.id.toString()
        );
        const group = [pid, ...childIds];
        const parentSel = selectionModel.includes(pid);
        const selChildren = childIds.filter((id) =>
          selectionModel.includes(id)
        );
        const anySel = parentSel || selChildren.length > 0;

        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ByzMuiCheckBox
              size="small"
              checked={parentSel && selChildren.length === childIds.length}
              indeterminate={
                anySel && !(parentSel && selChildren.length === childIds.length)
              }
              onChange={() =>
                setSelectionModel((prev) => {
                  const allSel = group.every((id) => prev.includes(id));
                  if (allSel) return prev.filter((id) => !group.includes(id));
                  return [...prev, ...group.filter((id) => !prev.includes(id))];
                })
              }
            />
            <ByzMuiOpenProcessMenu
              hideHeaderTitle
              menuItems={[
                {
                  label: t("patient-diet-procedures.homepage.send-selected"),
                  disabled: !anySel,
                  onClick: handleSendSelected,
                },
                {
                  label: t("patient-diet-procedures.homepage.delete-selected"),
                  disabled: !anySel,
                  onClick: () => {
                    setExported((prev) => {
                      if (!prev) return prev;
                      const parents = parentSel
                        ? prev.parentRows.filter((r) => r.id.toString() !== pid)
                        : prev.parentRows;
                      const cm = { ...prev.childMap };
                      if (selChildren.length) {
                        cm[key] = cm[key].filter(
                          (r) => !selChildren.includes(r.id.toString())
                        );
                      }
                      return { ...prev, parentRows: parents, childMap: cm };
                    });
                    setSelectionModel((prev) =>
                      prev.filter(
                        (id) => id !== pid && !selChildren.includes(id)
                      )
                    );
                    setChildApproval((prev) => {
                      const nxt = { ...prev };
                      selChildren.forEach((id) => delete nxt[id]);
                      return nxt;
                    });
                  },
                  divider: true,
                },
                {
                  label: t("patient-diet-procedures.homepage.hold-selected"),
                  disabled: !anySel,
                  onClick: () => {
                    if (parentSel) setParentApproval("Beklemede");
                    if (selChildren.length) {
                      setChildApproval((prev) => {
                        const nxt = { ...prev };
                        selChildren.forEach((id) => (nxt[id] = "Beklemede"));
                        return nxt;
                      });
                    }
                  },
                  divider: true,
                },
              ]}
            />
          </Box>
        );
      },
    }),
    [
      displayedChildMap,
      selectionModel,
      allRowIds,
      isAllSelected,
      isIndeterminate,
      handleSendSelected, 
      t,          
    ]
  );
  const indentColumn = useMemo<GridColDef>(() => ({
    field: "__indent__",
    headerName: "",
    width: 60,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    renderCell: (params) => {
      const rowIds = Array.from(params.api.getRowModels().keys());
      const isFirstDetailRow = rowIds[0] === params.id;
      if (!isFirstDetailRow) {
        return null;
      }
      return (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box component="img" src="/assets/icons/vector/line.svg" />
        </Box>
      );
    },
  }), []);
  const childActionsColumn: GridColDef = useMemo(
    () => ({
      field: "__actions__",
      headerName: "",
      width: 100,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderHeader: () => null,
      renderCell: (params) => {
        const cid = params.id.toString();
        const key = params.row.diyetZamani as string;
        const isSel = selectionModel.includes(cid);
        const status = childApproval[cid] || "Beklemede";

        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ByzMuiCheckBox
              size="small"
              checked={isSel}
              onChange={() =>
                setSelectionModel((prev) =>
                  prev.includes(cid)
                    ? prev.filter((x) => x !== cid)
                    : [...prev, cid]
                )
              }
            />
            <ByzMuiOpenProcessMenu
              hideHeaderTitle
              menuItems={[
                {
                  label: t(
                    "patient-diet-procedures.homepage.send-diet-request"
                  ),
                  disabled: !isSel || status === "Gönderildi",
                  onClick: () =>
                    setChildApproval((prev) => ({
                      ...prev,
                      [cid]: "Gönderildi",
                    })),
                },
                {
                  label: t(
                    "patient-diet-procedures.homepage.delete-diet-request"
                  ),
                  disabled: !isSel,
                  onClick: () => {
                    setExported((prev) => {
                      if (!prev) return prev;
                      const cm = { ...prev.childMap };
                      cm[key] = cm[key].filter((r) => r.id.toString() !== cid);
                      return { ...prev, childMap: cm };
                    });
                    setChildApproval((prev) => {
                      const nxt = { ...prev };
                      delete nxt[cid];
                      return nxt;
                    });
                    setSelectionModel((prev) => prev.filter((x) => x !== cid));
                  },
                  divider: true,
                },
                {
                  label: t(
                    "patient-diet-procedures.homepage.hold-diet-request"
                  ),
                  disabled: !isSel || status === "Beklemede",
                  onClick: () =>
                    setChildApproval((prev) => ({
                      ...prev,
                      [cid]: "Beklemede",
                    })),
                  divider: true,
                },
              ]}
            />
          </Box>
        );
      },
    }),
    [selectionModel, childApproval, t]
  );

  const detailToggleColumn = useMemo(
    () => ({
      ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
      headerName: "",
      width: 60,
      disableColumnMenu: true,
    }),
    []
  );

  const [parentCols, setParentCols] = useState<GridColDef[]>([]);
  const [childCols, setChildCols] = useState<GridColDef[]>([]);

  useEffect(() => {
    if (!exported) return;
    const pCols = exported.parentColumns.map((c) =>
      c.field === "onayDurumu"
        ? {
            ...c,
            renderCell: () => (
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  bgcolor:
                    parentApproval === "Beklemede" ? "#FFCC004D" : "#AEADA64D",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {parentApproval}
              </Box>
            ),
          }
        : c
    );
    const cCols = exported.childColumns.map((c) => {
      if (c.field === "onayDurumu") {
        return {
          ...c,
          renderCell: ({ id }: any) => {
            const st = childApproval[id.toString()] || "Beklemede";
            return (
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  bgcolor: st === "Beklemede" ? "#FFCC004D" : "#AEADA64D",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {st}
              </Box>
            );
          },
        };
      }
      if (c.field === "duzenle") {
        //IDOGAN;452471;05052025;DUZENLE SATIRI ICIN, BUTONUN KULLANILABILIRLIGI DURUMA GORE DEGISMESI SAGLANDI.
        return {
          ...c,
          renderCell: (params: any) => {
            const cid = params.id.toString();
            const status = childApproval[cid] || "Beklemede";
            const isDisabled = status === "Gönderildi";

            return (
              <ByzGrdEditButton
                disabled={isDisabled}
                onClick={() => {
                  if (!isDisabled) {
                    setEditRow(params.row);
                    setEditDialogOpen(true);
                  }
                }}
              />
            );
          },
        };
      }

      return c;
    });
    setParentCols([parentActionsColumn, detailToggleColumn, ...pCols]);
    setChildCols([indentColumn, childActionsColumn, ...cCols]);
  }, [
    exported,
    parentApproval,
    childApproval,
    parentActionsColumn,
    childActionsColumn,
    detailToggleColumn,
    indentColumn,
  ]);

  const handleColumnResize = useCallback(({ colDef, width }: any) => {
    const { field } = colDef;
    setParentCols((cols) =>
      cols.map((c) =>
        c.field === field ? { ...c, width, flex: undefined } : c
      )
    );
    setChildCols((cols) =>
      cols.map((c) =>
        c.field === field ? { ...c, width, flex: undefined } : c
      )
    );
  }, []);

  return (
    <>
      <PatientDatas isOpenMedCard isCreateMedCard />
      <GeneralHealthInformation />

      <Box sx={{ width: "100%" }}>
        <Grid container spacing={2}>
          <Grid xs={12}>
            <Card sx={{ p: 2, mb: 2 }}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="flex-start"
                mb={2}
                sx={{
                  pb: 1, //
                  borderBottom: "1px solid #E0E0E0",
                }}
              >
                <ByzMuiTypography isTitle>
                  {t("patient-diet-procedures.homepage.diet-planning")}
                </ByzMuiTypography>
                <ByzMuiTypography
                  sx={{ fontStyle: "italic", fontSize: 14, ml: "30px" }}
                >
                  {t("patient-diet-procedures.homepage.date")}{" "}
                  {format(addDays(new Date(), dayOffset), "dd.MM.yyyy")}
                </ByzMuiTypography>
              </Box>

              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box display="flex" alignItems="center" gap={3}>
                  <ByzMuiAddButton
                    onClick={() => setAddDialogOpen(true)}
                    popup
                  />
                  <ByzDaySelectButton
                    previousText={t(
                      "patient-diet-procedures.homepage.previous-day"
                    )}
                    todayText={t("patient-diet-procedures.homepage.today")}
                    nextText={t("patient-diet-procedures.homepage.next-day")}
                    onChange={(d) =>
                      setDayOffset((prev) =>
                        d === "previous"
                          ? prev - 1
                          : d === "next"
                          ? prev + 1
                          : 0
                      )
                    }
                  />
                  <ByzMuiSendButton onClick={handleSendSelected} />
                </Box>

                <Box display="flex" alignItems="center" gap={1}>
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ borderColor: "black", width: "2px" }}
                  />
                  <ByzMuiPrintButton type={1} />
                  <ByzMuiPrintButton type={2} />
                  <ByzMuiPrintButton type={3} />
                </Box>
              </Box>
            </Card>

            <Card sx={{ p: 2 }}>
              <Box
                mb={2}
                sx={{
                  pb: 1, //
                  borderBottom: "1px solid #E0E0E0",
                }}
              >
                <ByzMuiTypography isTitle>
                  {t("patient-diet-procedures.homepage.diet")}
                </ByzMuiTypography>
              </Box>
              {exported ? (
                <ByzMuiDataGrid
                  rows={displayedParentRows}
                  columns={parentCols}
                  pageSize={5}
                  disableProcessMenu
                  onColumnWidthChange={handleColumnResize}
                  slots={{
                    detailPanelExpandIcon: () => (
                      <img
                        src="/assets/icons/vector/vector-down.svg"
                        alt="down"
                      />
                    ),
                    detailPanelCollapseIcon: () => (
                      <img
                        src="/assets/icons/vector/vector-right.svg"
                        alt="right"
                      />
                    ),
                  }}
                  getDetailPanelContent={({ row }: any) => (
                    <Box sx={{ p: 0, ml: "0px" }}>
                      <ByzMuiDataGrid
                        rows={displayedChildMap[row.diyetZamani] || []}
                        columns={childCols}
                        pageSize={5}
                        hideFooter
                        autoHeight
                        disableProcessMenu
                        onColumnWidthChange={handleColumnResize}
                        sx={{
                          "& .MuiDataGrid-columnHeaders": { display: "none" },
                        }}
                      />
                    </Box>
                  )}
                  getDetailPanelHeight={({ row }: any) => {
                    return "auto";
                  }}
                  rowSelectionModel={selectionModel}
                  onRowSelectionModelChange={(m) =>
                    setSelectionModel(m.map((id) => id.toString()))
                  }
                  disableRowSelectionOnClick
                />
              ) : (
                <ByzMuiTypography color="gray"></ByzMuiTypography>
              )}
            </Card>
          </Grid>
        </Grid>
      </Box>

      <OpenComponentDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        hideHeader
      >
        <DietPopup
          onClose={() => setAddDialogOpen(false)}
          onExportGrid={handleExportGrid}
        />
      </OpenComponentDialog>

      <OpenComponentDialog //IDOGAN;452471;05052025
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        title={editRow?.adi ?? ""}
        showSaveButton
        onSaveClick={() => {
          console.log("Düzenlenen satır:", editRow);
          setEditDialogOpen(false);
        }}
      >
        <EditPage rowData={editRow} onClose={() => setEditDialogOpen(false)} />
      </OpenComponentDialog>
    </>
  );
}
